/**
 * Data layer for the COMPANY panel's Meditation Sounds section.
 *
 * Backed by the company/v1 meditation-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Every
 * fetcher takes an optional `department` so the section's filter can narrow
 * the whole page to one department.
 *
 * The adapter is exported because the admin-side Meditation Hub is served by
 * the same builders in services/meditationHubService.js and should adapt the
 * payload through here rather than restating the field mapping.
 */

import {
  getCompanyMeditationDashboard,
  getCompanyMeditationUsers,
  getCompanyMeditationUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Play-ledger block shared by every meditation dashboard. */
export const adaptMeditationListening = (data) => ({
  listeners: data.listeners,
  totalPlays: data.totalPlays,
  totalPlayTimeMins: data.totalPlayTimeMins,
  avgSessionMins: data.avgSessionMins,
  avgPlaysPerListener: data.avgPlaysPerListener,
  mostPlayedMeditation: data.mostPlayedMeditation,
  topTenSharePercentage: data.topTenSharePercentage,
  meditationsPlayed: data.meditationsPlayed ?? 0,
  mostPlayed: (data.mostPlayed || []).map((meditation) => ({
    id: meditation._id,
    name: meditation.name,
    plays: meditation.plays,
    listeners: meditation.listeners ?? null,
    playTimeMins: meditation.playTimeMins,
    rating: meditation.rating ?? null,
    expert: meditation.expert || null
  })),
  // Backend zero-fills the last 30 days; charts consume plain series.
  playsTrend: (data.playsTrend || []).map((day) => day.plays),
  listenMinsTrend: (data.playsTrend || []).map((day) => day.listenMins || 0),
  weekdayPlays: data.weekdayPlays || [],
  sessionLengthDistribution: data.sessionLengthDistribution || [],
  listenerEngagement: data.listenerEngagement || [],
  formatSplit: data.formatSplit || [],
  topExperts: data.topExperts || [],
  topCategories: data.topCategories || []
});

export const fetchCompanyMeditationDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyMeditationDashboard(department));
    return {
      ...adaptMeditationListening(data),
      // Company-only: the size of the population, so the page can say
      // "32 of 240 employees have meditated".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyMeditationDashboard error:', error);
    return null;
  }
};

// Matches the MeditationUsersTable fetcher contract.
export const fetchCompanyMeditationUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyMeditationUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyMeditationUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's meditation profile ({ user, mostPlayedMeditation,
 * topMeditations, meditationHistory }). The backend 404s any user outside this
 * company, so no scoping is needed here.
 */
export const fetchCompanyMeditationUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyMeditationUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyMeditationUserDetail error:', error);
    return null;
  }
};
